import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, Trash2, RefreshCw, CheckCircle2, Circle } from "lucide-react";
import { FlowDrop } from "@/components/washflow/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function Admin() {
  const [token, setToken] = useState(localStorage.getItem("wf_admin_token"));
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [leads, setLeads] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/leads`, { headers: { Authorization: `Bearer ${token}` } });
      setLeads(data);
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.removeItem("wf_admin_token");
        setToken(null);
      } else {
        toast.error("Could not load enquiries");
      }
    }
  }, [token]);

  useEffect(() => {
    if (token) load();
  }, [token, load]);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem("wf_admin_token", data.token);
      setToken(data.token);
    } catch (e2) {
      setError(formatApiError(e2.response?.data?.detail));
    }
  };

  const logout = () => {
    localStorage.removeItem("wf_admin_token");
    setToken(null);
    setLeads(null);
  };

  const toggleStatus = async (lead) => {
    const next = lead.status === "contacted" ? "new" : "contacted";
    try {
      await axios.patch(`${API}/leads/${lead.id}`, { status: next }, { headers: { Authorization: `Bearer ${token}` } });
      setLeads(leads.map((l) => (l.id === lead.id ? { ...l, status: next } : l)));
    } catch {
      toast.error("Could not update status");
    }
  };

  const remove = async (lead) => {
    try {
      await axios.delete(`${API}/leads/${lead.id}`, { headers: { Authorization: `Bearer ${token}` } });
      setLeads(leads.filter((l) => l.id !== lead.id));
      toast.success("Enquiry deleted");
    } catch {
      toast.error("Could not delete enquiry");
    }
  };

  if (!token) {
    return (
      <main className="min-h-screen bg-[#0F2B3E] flex items-center justify-center px-4">
        <form data-testid="admin-login-form" onSubmit={login} className="w-full max-w-md bg-[#1B4F72] rounded-2xl p-8 space-y-5">
          <div className="flex items-center gap-3 justify-center">
            <FlowDrop size={40} dark />
            <span className="font-display font-extrabold text-xl text-white">WashFlow Admin</span>
          </div>
          <p className="text-center text-sm text-[#AED6F1]">OpsIQ team only — sign in to view enquiries</p>
          {error && <p data-testid="admin-login-error" className="text-center text-sm font-bold text-red-300 bg-red-500/10 rounded-lg py-2">{error}</p>}
          <div>
            <label className="block text-sm font-bold text-[#EBF5FB] mb-1.5" htmlFor="admin-email">Email</label>
            <input id="admin-email" data-testid="admin-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-xl border border-[#E2E8F0] bg-white px-4 py-3.5 text-base text-[#2C3E50] focus:outline-none focus:ring-2 focus:ring-[#1ABC9C]" placeholder="admin@opsiq.com.gh" />
          </div>
          <div>
            <label className="block text-sm font-bold text-[#EBF5FB] mb-1.5" htmlFor="admin-password">Password</label>
            <input id="admin-password" data-testid="admin-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-xl border border-[#E2E8F0] bg-white px-4 py-3.5 text-base text-[#2C3E50] focus:outline-none focus:ring-2 focus:ring-[#1ABC9C]" placeholder="••••••••" />
          </div>
          <button type="submit" data-testid="admin-login-submit-btn" className="w-full bg-[#1ABC9C] hover:bg-[#16A085] text-white text-lg font-bold px-8 py-4 rounded-full transition-colors">
            Sign in
          </button>
          <a href="/" className="block text-center text-sm text-[#AED6F1] hover:text-white transition-colors">← Back to proposal</a>
        </form>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-[#F8FAFC]">
      <header className="bg-[#1B4F72] sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <FlowDrop size={32} dark />
            <span className="font-display font-extrabold text-lg text-white">Enquiries</span>
            {leads && <span data-testid="admin-leads-count" className="text-xs font-bold bg-[#1ABC9C] text-white rounded-full px-2.5 py-0.5">{leads.length}</span>}
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="admin-refresh-btn" onClick={load} className="p-2 text-[#AED6F1] hover:text-white transition-colors" aria-label="Refresh">
              <RefreshCw className="w-5 h-5" />
            </button>
            <button data-testid="admin-logout-btn" onClick={logout} className="inline-flex items-center gap-2 text-sm font-bold text-white bg-white/10 hover:bg-white/20 px-4 py-2 rounded-full transition-colors">
              <LogOut className="w-4 h-4" /> Log out
            </button>
          </div>
        </div>
      </header>
      <div data-testid="admin-dashboard" className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        {leads === null ? (
          <p className="text-center text-[#5D6D7E] py-20">Loading enquiries…</p>
        ) : leads.length === 0 ? (
          <div className="text-center py-20">
            <p className="font-display text-xl font-bold text-[#2C3E50]">No enquiries yet</p>
            <p className="text-[#5D6D7E] mt-2">New submissions from the proposal contact form will appear here.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {leads.map((lead, i) => (
              <div key={lead.id} data-testid={`lead-row-${i}`} className="bg-white rounded-2xl border border-[#E2E8F0] p-6">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-3 flex-wrap">
                      <h3 className="font-display text-lg font-bold text-[#2C3E50]">{lead.laundry_name}</h3>
                      <span data-testid={`lead-status-${i}`} className={`text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full ${lead.status === "contacted" ? "bg-[#E8F8F5] text-[#16A085]" : "bg-[#EBF5FB] text-[#1B4F72]"}`}>
                        {lead.status === "contacted" ? "Contacted" : "New"}
                      </span>
                    </div>
                    <p className="mt-1 text-base text-[#2C3E50]">{lead.contact_name} · <a href={`tel:${lead.phone}`} className="text-[#1B4F72] font-semibold">{lead.phone}</a></p>
                    <p className="text-sm text-[#5D6D7E]">{lead.location} · {lead.branches} branch{lead.branches > 1 ? "es" : ""} · Interested in: <span className="font-semibold">{lead.tier}</span></p>
                    {lead.message && <p className="mt-2 text-sm text-[#5D6D7E] italic">“{lead.message}”</p>}
                    <p className="mt-2 text-xs text-[#94A3B8]">{new Date(lead.created_at).toLocaleString("en-GH")}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button data-testid={`lead-status-toggle-${i}`} onClick={() => toggleStatus(lead)}
                      className={`inline-flex items-center gap-1.5 text-sm font-bold px-4 py-2 rounded-full transition-colors ${lead.status === "contacted" ? "bg-[#F8FAFC] text-[#5D6D7E] border border-[#E2E8F0]" : "bg-[#1ABC9C] text-white hover:bg-[#16A085]"}`}>
                      {lead.status === "contacted" ? <><Circle className="w-4 h-4" /> Mark new</> : <><CheckCircle2 className="w-4 h-4" /> Mark contacted</>}
                    </button>
                    <button data-testid={`lead-delete-${i}`} onClick={() => remove(lead)} className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-full transition-colors" aria-label="Delete enquiry">
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </main>
  );
}
